import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSession } from "../lib/session";

export default function ResetPassword(): JSX.Element {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const reload = useSession((s) => s.reload);
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null);
    if (password !== confirm) {
      setErr("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch("/api/auth/reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token, password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { error?: string }).error ?? "Reset failed.");
      }
      await reload();
      nav("/dashboard", { replace: true });
    } catch (e2) {
      setErr((e2 as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <section className="login-card card stack" style={{ maxWidth: 420, margin: "60px auto" }}>
        <h1>Reset password</h1>
        <p>This link is missing its token. Request a new one from the <Link to="/login">sign-in page</Link>.</p>
      </section>
    );
  }

  return (
    <section className="login-card card stack" style={{ maxWidth: 420, margin: "60px auto" }}>
      <h1>Choose a new password</h1>
      <form onSubmit={submit} className="stack" style={{ gap: 10 }}>
        <label className="stack" style={{ gap: 4 }}>
          <span className="login-lbl">New password</span>
          <input
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="login-input"
            autoComplete="new-password"
            placeholder="min 8 chars, 1 letter, 1 number"
          />
        </label>
        <label className="stack" style={{ gap: 4 }}>
          <span className="login-lbl">Confirm password</span>
          <input
            type="password"
            required
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="login-input"
            autoComplete="new-password"
          />
        </label>
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? "Saving…" : "Set new password"}
        </button>
        {err ? <p className="login-err">{err}</p> : null}
      </form>
      <style>{`
        .login-card { background: var(--bg-card, #16161f); border: 1px solid var(--line, #2a2a3d); color: var(--text-pri, #EFEFF1); }
        .login-card h1 { color: var(--text-pri); margin-top: 0; }
        .login-lbl { font-family: var(--f-mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--text-mute); }
        .login-input { padding: 10px 12px; background: var(--bg-deep, #0e0e10); border: 1px solid var(--line); color: var(--text-pri); border-radius: 6px; font: inherit; font-size: 14px; width: 100%; }
        .login-input:focus { outline: 1px solid var(--gold, #FFD700); outline-offset: -1px; }
        .login-err { color: var(--danger, #ff5252); font-size: 13px; margin: 0; }
      `}</style>
    </section>
  );
}
